import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, FolderKanban, ArrowRight, MoreVertical, Trash2, FolderPlus } from "lucide-react";

export default function Portfolios() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/portfolios");
      setItems(data);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createPortfolio = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data: p } = await api.post("/portfolios", { name, description: desc });
      toast.success(t("portfolioCreated"));
      setOpen(false); setName(""); setDesc("");
      navigate(`/app/portfolios/${p.id}`);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/portfolios/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success(t("delete"));
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    }
  };

  const createDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="portfolios-new-button">
          <Plus className="h-4 w-4" /> {t("newPortfolio")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("createPortfolio")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("portfolioName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="portfolio-name-input" placeholder="Tech Domains" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("portfolioDesc")} <span className="text-xs text-muted-foreground">({t("optional")})</span></Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="portfolio-desc-input" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button onClick={createPortfolio} disabled={creating || !name.trim()} data-testid="portfolio-create-submit">
            {creating ? t("loading") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">{t("yourPortfolios")}</h1>
        {createDialog}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><FolderPlus className="h-6 w-6" /></span>
          <h3 className="font-heading text-lg font-semibold">{t("noPortfolios")}</h3>
          <p className="max-w-sm text-sm text-muted-foreground">{t("noPortfoliosDesc")}</p>
          {createDialog}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolios-grid">
          {items.map((p) => (
            <Card key={p.id} className="card-lift flex flex-col p-5" data-testid="portfolio-card">
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FolderKanban className="h-5 w-5" /></span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="portfolio-menu"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => remove(p.id)} data-testid="portfolio-delete">
                      <Trash2 className="mr-2 h-4 w-4" /> {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{p.name}</h3>
              {p.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.description}</p>}
              <div className="mt-auto flex items-center justify-between pt-4">
                <span className="text-sm text-muted-foreground">{p.domain_count} {t("domainsCount")}</span>
                <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate(`/app/portfolios/${p.id}`)} data-testid="portfolio-open">
                  {t("open")} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
